import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EnhancedCharacter } from '../../utils/ClassComponentRegistry';
import { loadClassById } from '../../utils/classDataLoader';
import { EnhancedClass, Subclass } from '../../types/class-data';
import { calculateTierFromLevel, isFeatureAvailable } from '../../utils/levelTierMapping';
import SubclassFeatures from './ClassFeatures/SubclassFeatures';
import ContentText from '../Common/ContentText';
import './ClassFeaturesDisplay.css';

interface ClassFeaturesDisplayProps {
  enhancedCharacter: EnhancedCharacter;
}

const ClassFeaturesDisplay: React.FC<ClassFeaturesDisplayProps> = ({ 
  enhancedCharacter
}) => {
  const [classData, setClassData] = useState<EnhancedClass | null>(null);
  const [currentSubclass, setCurrentSubclass] = useState<Subclass | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedFeatures, setExpandedFeatures] = useState<Set<number>>(new Set());

  const character = enhancedCharacter.character;

  const toggleFeatureExpansion = (featureIndex: number) => {
    setExpandedFeatures(prev => {
      const newSet = new Set(prev);
      if (newSet.has(featureIndex)) {
        newSet.delete(featureIndex);
      } else {
        newSet.add(featureIndex);
      }
      return newSet;
    });
  };


  useEffect(() => {
    const loadClassData = async () => {
      if (!character?.class) {
        setLoading(false);
        return;
      }

      try {
        const data = await loadClassById(character.class);
        setClassData(data);

        // Find current subclass if set
        if (data && character.subclass) {
          const subclass = data.subclasses.find(sub => sub.id === character.subclass);
          setCurrentSubclass(subclass || null);
        }
      } catch (error) {
        console.error('Failed to load class data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadClassData();
  }, [character?.class, character?.subclass]);

  if (!character) {
    return (
      <motion.div 
        className="class-features"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="class-features__loading">
          Loading character...
        </div>
      </motion.div>
    );
  }

  if (loading) {
    return (
      <motion.div 
        className="class-features"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="class-features__loading">
          Loading class features...
        </div>
      </motion.div>
    );
  }

  if (!classData) {
    return (
      <motion.div 
        className="class-features"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="class-features__content">
          <p>Class data not found for: {character.class}</p>
        </div>
      </motion.div>
    );
  }

  const characterLevel = parseInt(character.level) || 1;
  const characterTier = calculateTierFromLevel(characterLevel);

  // Get class features that should be displayed at current level
  const availableClassFeatures = classData.classFeatures?.filter(feature => {
    // If feature has no tier specified, show it at tier 1
    const featureTier = (feature as any).tier || 1;
    return isFeatureAvailable(featureTier, characterLevel);
  }) || [];

  // Add Hope feature if it exists
  const hopeFeature = classData.hopeFeature;

  return (
    <motion.div
      className="class-features"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.15 }}
    >
      {/* Class Features - Direct children, no wrapper divs */}
          
      {/* Core Class Features */}
      <AnimatePresence>
        {availableClassFeatures.map((feature, index) => {
          const isExpanded = expandedFeatures.has(index);

          return (
            <motion.div
              key={index}
              className="class-feature"
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ delay: 0.05 + index * 0.1, duration: 0.15 }}
              layout
            >
              <motion.div
                className="class-feature__header"
                onClick={() => toggleFeatureExpansion(index)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <h4 className="class-feature__title">
                  <span>{feature.name.toUpperCase()}</span>
                  <span className={`collapsible-section__icon ${isExpanded ? 'collapsible-section__icon--expanded' : ''}`}>
                    ▼
                  </span>
                </h4>
              </motion.div>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    className="class-feature__description"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <ContentText content={feature.description} />
                    </motion.div>
                    {feature.uses && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15 }}
                      >
                        <strong>Uses:</strong> {feature.uses}
                      </motion.p>
                    )}
                    {feature.progression && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                      >
                        <em>{feature.progression}</em>
                      </motion.p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </AnimatePresence>

      {/* Hope Feature */}
      {hopeFeature && (
        <motion.div
          className="class-feature"
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.1 + availableClassFeatures.length * 0.1, duration: 0.1 }}
          layout
        >
          <motion.div
            className="class-feature__header"
            whileHover={{ scale: 1.02 }}
          >
            <h4 className="class-feature__title">{hopeFeature.name.toUpperCase()}</h4>
          </motion.div>
          <motion.div
            className="class-feature__description"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 + availableClassFeatures.length * 0.1 }}
          >
            <p>{hopeFeature.description}</p>
            <p><strong>Hope Cost:</strong> {hopeFeature.cost}</p>
          </motion.div>
        </motion.div>
      )}

      {/* Tier-based Features (if any need to be added dynamically) */}
      {characterTier >= 2 && (
        <motion.div
          className="class-feature"
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.15 + availableClassFeatures.length * 0.1, duration: 0.2 }}
          layout
        >
          <motion.div
            className="class-feature__header"
            whileHover={{ scale: 1.02 }}
          >
            <h4 className="class-feature__title">TIER {characterTier} ADVANCEMENT</h4>
          </motion.div>
          <motion.div
            className="class-feature__description"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + availableClassFeatures.length * 0.1 }}
          >
            <p>You have reached Tier {characterTier} and gained additional capabilities.</p>
            <p><em>Tier advancement unlocks new features and improves existing ones.</em></p>
          </motion.div>
        </motion.div>
      )}

      {/* Subclass Features */}
      {currentSubclass && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 + availableClassFeatures.length * 0.1, duration: 0.2 }}
        >
          <SubclassFeatures
            subclass={currentSubclass}
            characterLevel={characterLevel}
            characterTier={characterTier}
            subclassCardTier={character.subclassCardTier || 'foundation'}
          />
        </motion.div>
      )}
    </motion.div>
  );
};

export default ClassFeaturesDisplay;